import { useMutation, useQuery } from "@apollo/client";
import { PauseIcon, PlayIcon, XCircleIcon } from "@heroicons/react/outline";
import { compact as compactLodash } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import {
  AddBotToMeetingNewPageMutationMutation,
  AddBotToMeetingNewPageMutationMutationVariables,
  ChangeBotRecordingStatusNewPageMutationMutation,
  ChangeBotRecordingStatusNewPageMutationMutationVariables,
  GetMeetingBotStatusQueryQuery,
  GetMeetingBotStatusQueryQueryVariables,
  GetMeetingTranscriptQueryQuery,
  MeetingViewMeetingNodeNewPageFragmentFragment,
  OnlineVideoConference,
  RemoveBotFromMeetingNewPageMutationMutation,
  RemoveBotFromMeetingNewPageMutationMutationVariables,
} from "types/graphql-schema";

import addBotToMeetingMutation from "@apps/meeting-new/graphql/add-bot-to-meeting-mutation";
import changeBotRecordingStatusMutation from "@apps/meeting-new/graphql/change-bot-recording-status-mutation";
import removeBotFromMeetingMutation from "@apps/meeting-new/graphql/remove-bot-from-meeting-mutation";
import Button, { ButtonProps } from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import Loading from "@components/loading/loading";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { videoConferenceName } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { removeCurrentYear } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";
import { isPageHidden } from "@helpers/hooks/use-page-visible";
import { titleCase } from "@helpers/string";

import getMeetingBotStatusQuery from "../graphql/get-meeting-bot-status-query";

const pollInterval = 20 * 1000; // fetch every 15s

export const MeetingBot = ({
  meeting,
  textClassName,
  buttonProps = {},
}: {
  textClassName?: string;
  meeting:
    | MeetingViewMeetingNodeNewPageFragmentFragment
    | NonNullable<GetMeetingTranscriptQueryQuery["meeting"]>;
  buttonProps?: ButtonProps;
}) => {
  const meetingId = meeting.id;
  const [localAddBotToMeetingLoading, setLocalAddBotToMeetingLoading] =
    useState(false);
  const [
    localChangeBotRecordingStatusLoading,
    setLocalChangeBotRecordingStatusLoading,
  ] = useState(false);
  const [
    localRemoveBotFromMeetingLoading,
    setLocalRemoveBotFromMeetingLoading,
  ] = useState(false);

  const [addBotToMeeting, { loading: addBotToMeetingLoading }] = useMutation<
    AddBotToMeetingNewPageMutationMutation,
    AddBotToMeetingNewPageMutationMutationVariables
  >(addBotToMeetingMutation);
  const [removeBotFromMeeting, { loading: removeBotFromMeetingLoading }] =
    useMutation<
      RemoveBotFromMeetingNewPageMutationMutation,
      RemoveBotFromMeetingNewPageMutationMutationVariables
    >(removeBotFromMeetingMutation);
  const [
    changeBotRecordingStatus,
    { loading: changeBotRecordingStatusLoading },
  ] = useMutation<
    ChangeBotRecordingStatusNewPageMutationMutation,
    ChangeBotRecordingStatusNewPageMutationMutationVariables
  >(changeBotRecordingStatusMutation);

  const { hasBotInMeeting, botIsRecording, botIsWaitingToJoinMeeting } =
    meeting;
  // const { hasBotInMeeting, botIsRecording, botIsWaitingToJoinMeeting } = {
  //   hasBotInMeeting: true,
  //   botIsRecording: true,
  //   botIsWaitingToJoinMeeting: true,
  // };

  const loadingUI =
    botIsWaitingToJoinMeeting ||
    // local loading
    localAddBotToMeetingLoading ||
    localChangeBotRecordingStatusLoading ||
    localRemoveBotFromMeetingLoading ||
    // mutation loading
    addBotToMeetingLoading ||
    changeBotRecordingStatusLoading ||
    removeBotFromMeetingLoading;

  // The meeting bot states will be updated and propagated by pusher but just in case
  // we refresh (with the query below) the meeting bot states regularly when showing the loading UI.
  // The mutations do not return the actual bot state, recall api calls are done asynchronously.
  useQuery<
    GetMeetingBotStatusQueryQuery,
    GetMeetingBotStatusQueryQueryVariables
  >(getMeetingBotStatusQuery, {
    skip: !loadingUI,
    skipPollAttempt: isPageHidden,
    pollInterval,
    variables: { meetingId },
  });

  const { confirm, ConfirmationDialog } = useConfirm(
    "Are you sure you want to remove the Notetaker?",
    "Removing the Notetaker will stop Topicflow from recording notes used for creating the meeting recap.",
  );

  // Reset loading state when bot status changes
  useEffect(() => {
    if (localAddBotToMeetingLoading) {
      setLocalAddBotToMeetingLoading(false);
    }
    if (localChangeBotRecordingStatusLoading) {
      setLocalChangeBotRecordingStatusLoading(false);
    }
    if (localRemoveBotFromMeetingLoading) {
      setLocalRemoveBotFromMeetingLoading(false);
    }
  }, [botIsRecording, botIsWaitingToJoinMeeting, hasBotInMeeting]);

  const willJoinAtTime =
    meeting.meetingGroup?.addTranscriptionBotToMeetingsByDefault &&
    meeting.startDatetime &&
    moment().isBefore(meeting.startDatetime)
      ? meeting.startDatetime
      : null;
  const willJoinToday =
    willJoinAtTime && moment(willJoinAtTime).isSame(moment(), "day");
  const conferenceVideoName = meeting.videoConferenceType
    ? videoConferenceName[meeting.videoConferenceType]
    : "None";

  const handleClickAddBotInMeeting = () => {
    setLocalAddBotToMeetingLoading(true);
    addBotToMeeting({
      variables: { meetingId },
    });
  };

  const handleClickRemoveBotInMeeting = async () => {
    const confirmed = await confirm();
    if (confirmed) {
      setLocalRemoveBotFromMeetingLoading(true);
      removeBotFromMeeting({
        variables: { meetingId },
      });
    }
  };

  const handleClickPauseBotRecording = () => {
    setLocalChangeBotRecordingStatusLoading(true);
    const isRecording = !botIsRecording;
    changeBotRecordingStatus({
      variables: { meetingId, isRecording },
    });
  };

  const meetingGroup = meeting.meetingGroup;
  if (!meetingGroup) return null;
  if (meeting.draft) return null;
  if (
    !meeting.videoConferenceType ||
    meeting.videoConferenceType === OnlineVideoConference.None
  )
    return null;

  const text = addBotToMeetingLoading
    ? `Notetaker joining ${titleCase(conferenceVideoName)}`
    : botIsWaitingToJoinMeeting
      ? "Notetaker waiting for admission"
      : (localChangeBotRecordingStatusLoading ||
            changeBotRecordingStatusLoading) &&
          !botIsRecording
        ? "Resuming"
        : (localChangeBotRecordingStatusLoading ||
              changeBotRecordingStatusLoading) &&
            botIsRecording
          ? "Pausing"
          : localRemoveBotFromMeetingLoading || removeBotFromMeetingLoading
            ? "Removing"
            : botIsRecording
              ? "Notetaker currently active"
              : hasBotInMeeting
                ? "Notetaker currently paused"
                : willJoinAtTime
                  ? `Notetaker will join${
                      willJoinToday
                        ? " "
                        : ` on ${removeCurrentYear(moment(willJoinAtTime).format("ll"))}`
                    } at ${moment(willJoinAtTime).format("H:mma").replace(":00", "")}`
                  : `Notetaker not in ${titleCase(conferenceVideoName)}`;

  return (
    <>
      <Dropdown
        align="end"
        gap={8}
        options={compactLodash([
          {
            type: "text",
            label: (
              <div className="flex items-center gap-2">
                <span
                  className={classNames(
                    "h-2 w-2 rounded-full shrink-0",
                    meeting.botIsRecording
                      ? "bg-red-500 drop-shadow-[0_0px_4px_rgba(239,68,68,0.25)] animate-pulse"
                      : "bg-gray-300",
                  )}
                />
                {text}
              </div>
            ),
          },
          {
            type: "divider",
          },
          !hasBotInMeeting && {
            label: (
              <span className="-ml-1 flex items-center gap-1">
                <TopicflowIcon type="plus" className="h-4 w-4 text-gray-500" />
                Add Notetaker
              </span>
            ),
            disabled: loadingUI,
            onClick: handleClickAddBotInMeeting,
          },
          !loadingUI &&
            hasBotInMeeting && {
              label: (
                <span className="-ml-1 flex items-center gap-1 text-nowrap">
                  {botIsRecording ? (
                    <PauseIcon className="h-4 w-4 text-gray-500" />
                  ) : (
                    <PlayIcon className="h-4 w-4 text-gray-500" />
                  )}
                  {botIsRecording ? "Pause Notetaker" : "Resume Notetaker"}
                </span>
              ),
              onClick: handleClickPauseBotRecording,
            },
          hasBotInMeeting && {
            label: (
              <span className="-ml-1 flex items-center gap-1">
                <XCircleIcon className="h-4 w-4 text-red-500" />
                Remove Notetaker
              </span>
            ),
            onClick: handleClickRemoveBotInMeeting,
          },
        ])}
      >
        <Button
          theme={buttonTheme.text}
          className={classNames(
            "gap-2",
            textClassName?.includes("hidden") &&
              "px-0 w-[24px] @3xl/layout-header:px-2 @3xl/layout-header:w-auto",
          )}
          {...buttonProps}
        >
          {loadingUI ? (
            <Loading size={4} mini />
          ) : hasBotInMeeting && botIsRecording ? (
            <span className="h-3 w-3 rounded-full shrink-0 ring-2 ring-red-600 bg-red-600 border-3 border-white" />
          ) : hasBotInMeeting && !botIsRecording ? (
            <TopicflowIcon
              type="pauseCircle"
              className="h-4 w-4 text-gray-700"
            />
          ) : (
            <TopicflowIcon
              type="microphone"
              className="h-4 w-4 text-gray-500"
            />
          )}
          <span className={textClassName}>Notetaker</span>
        </Button>
      </Dropdown>
      <ConfirmationDialog />
    </>
  );
};

export default MeetingBot;
